"use client";

import { Progress as ProgressPrimitive } from "@base-ui/react/progress";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "frontend-components/lib/utils";

const progressIndicatorVariants = cva(
    "rounded-pill h-full transition-[width] duration-500 ease-out",
    {
        variants: {
            color: {
                "teal-dark": "bg-surface-teal-dark",
                pink: "bg-chameleon-pink",
                yellow: "bg-chameleon-yellow",
                green: "bg-chameleon-green",
                greige: "bg-compendia-greige",
                "greige-dark": "bg-compendia-greige-dark",
                gradient: "bg-gradient-brand",
            },
        },
        defaultVariants: {
            color: "teal-dark",
        },
    },
);

type ProgressColor = NonNullable<VariantProps<typeof progressIndicatorVariants>["color"]>;

function Progress({
    className,
    children,
    value,
    color,
    ...props
}: ProgressPrimitive.Root.Props & {
    color?: ProgressColor;
}) {
    return (
        <ProgressPrimitive.Root
            value={value}
            data-slot="progress"
            className={cn("flex flex-wrap gap-3", className)}
            {...props}
        >
            {children}
            <ProgressTrack>
                <ProgressIndicator color={color} />
            </ProgressTrack>
        </ProgressPrimitive.Root>
    );
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
    return (
        <ProgressPrimitive.Track
            className={cn(
                "rounded-pill bg-compendia-greige-60 relative flex h-1 w-full items-center overflow-x-hidden",
                className,
            )}
            data-slot="progress-track"
            {...props}
        />
    );
}

function ProgressIndicator({
    className,
    color,
    ...props
}: ProgressPrimitive.Indicator.Props & {
    color?: ProgressColor;
}) {
    return (
        <ProgressPrimitive.Indicator
            data-slot="progress-indicator"
            className={cn(progressIndicatorVariants({ color }), className)}
            {...props}
        />
    );
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
    return (
        <ProgressPrimitive.Label
            className={cn("text-sm font-medium", className)}
            data-slot="progress-label"
            {...props}
        />
    );
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
    return (
        <ProgressPrimitive.Value
            className={cn("text-text-muted ml-auto text-sm tabular-nums", className)}
            data-slot="progress-value"
            {...props}
        />
    );
}

export {
    Progress,
    ProgressTrack,
    ProgressIndicator,
    ProgressLabel,
    ProgressValue,
    progressIndicatorVariants,
};
